import React, { useEffect, useRef, useState } from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

//  TODO:: This would be very useful feature for entire repo, we should make it more generic and move it to the generic folder
const InlineClampWithViewMore = ({
  text,
  maxLines,
  onViewMore
}: {
  text: string;
  maxLines: number;
  onViewMore: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureWithTailRef = useRef<HTMLDivElement | null>(null);
  const measureWithoutTailRef = useRef<HTMLDivElement | null>(null);
  const measureWithTailTextSpanRef = useRef<HTMLSpanElement | null>(null);
  const measureWithoutTailTextSpanRef = useRef<HTMLSpanElement | null>(null);

  const [displayText, setDisplayText] = useState<string>('');
  const [isClamped, setIsClamped] = useState<boolean>(false);

  const setMeasureWidthAndFont = (el: HTMLDivElement, container: HTMLElement) => {
    const rect = container.getBoundingClientRect();
    const computed = window.getComputedStyle(container);
    el.style.width = rect.width + 'px';
    // Ensure font metrics match
    // @ts-ignore
    if (computed.font) el.style.font = computed.font as any;
    el.style.lineHeight = computed.lineHeight;
    el.style.whiteSpace = 'normal';
  };

  const recalc = () => {
    const container = containerRef.current as HTMLDivElement | null;
    const withTail = measureWithTailRef.current as HTMLDivElement | null;
    const withoutTail = measureWithoutTailRef.current as HTMLDivElement | null;
    const withTailSpan = measureWithTailTextSpanRef.current as HTMLSpanElement | null;
    const withoutTailSpan = measureWithoutTailTextSpanRef.current as HTMLSpanElement | null;
    if (!container || !withTail || !withoutTail || !withTailSpan || !withoutTailSpan) return;

    setMeasureWidthAndFont(withTail, container);
    setMeasureWidthAndFont(withoutTail, container);

    const computed = window.getComputedStyle(container);
    const lineHeightPx = parseFloat(computed.lineHeight || '20');
    const maxHeight = lineHeightPx * maxLines;

    // Step 1: If the full text fits in maxLines, show it with no tail
    withoutTailSpan.textContent = text;
    const fullHeight = withoutTail.getBoundingClientRect().height;
    if (fullHeight <= maxHeight + 0.5) {
      setDisplayText(text);
      setIsClamped(false);
      return;
    }

    // Step 2: Find the largest prefix that fits WITH the tail "... View more" kept on one line
    let low = 0;
    let high = text.length;
    let best = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      withTailSpan.textContent = text.slice(0, mid);
      const h = withTail.getBoundingClientRect().height;
      if (h <= maxHeight + 0.5) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    setDisplayText(text.slice(0, best));
    setIsClamped(true);
  };

  useEffect(() => {
    recalc();
    const ro = new ResizeObserver(() => recalc());
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    recalc();
  }, [text]);

  return (
    <div ref={containerRef}>
      {!isClamped ? (
        <div style={{ WebkitFontSmoothing: 'antialiased' }}>{displayText}</div>
      ) : (
        <div style={{ WebkitFontSmoothing: 'antialiased' }}>
          {displayText}
          <span style={{ whiteSpace: 'nowrap' }}>
            ...
            <span>&nbsp;</span>
            <Text onClick={onViewMore} variant="subtitle2" inline link underlined>
              View more
            </Text>
          </span>
        </div>
      )}

      {/* hidden measurement containers */}
      <div
        ref={measureWithTailRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          zIndex: -1,
          pointerEvents: 'none',
          left: 0,
          top: 0
        }}
      >
        <span ref={measureWithTailTextSpanRef} />
        <span style={{ whiteSpace: 'nowrap' }}>
          ...
          <span>&nbsp;</span>
          <Text variant="subtitle2" inline link underlined>
            View more
          </Text>
        </span>
      </div>
      <div
        ref={measureWithoutTailRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          zIndex: -1,
          pointerEvents: 'none',
          left: 0,
          top: 0
        }}
      >
        <span ref={measureWithoutTailTextSpanRef} />
      </div>
    </div>
  );
};

export default InlineClampWithViewMore;
